import React, { useCallback, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { MetricCell } from './MetricCell';
import { useViewMetrics } from '@/features/trades/hooks/use-view-metrics';
import { useViewsStore, useTradesFilterStore, useTradesDataStore } from '@/stores';
import { useTradesSummary } from '@/hooks/trades/use-trades-summary';
import { buildTradesPillParams, buildTradesTermPillParams } from '@/utils/trades/map-server-params';
import { formatCurrency } from '@/utils/trades/mtm-calculations';
import { displayCashPool, withPoolSign } from '@/utils/trades/cash-pool-display';
import { applyLocalTradeFilters } from '@/utils/trades/apply-trade-filters';
import { applyTradeSearch } from '@/utils/trades/apply-trade-search';
import { CurrencyFilterPill } from '../CurrencyFilterPill';
import { TermFilterPill } from '../TermFilterPill';
import { MoneyMetricCell } from '../MoneyMetricCell';
import { ValueSkeleton } from '@/components/ui/ValueSkeleton';
import { agreementSections } from '@/utils/trades/agreement-sections';
import { ATTENTION_STATUSES } from '@/utils/trades/section-grouping';
import type { TradeType, FrameworkAgreement, TermType } from '@/types';

const STOCK_LB_FILTER: TradeType[] = ['lend', 'borrow'];
const REPO_FILTER: TradeType[] = ['repo', 'reverse_repo'];
// Per-metric exposure tooltips (LR-457 p.15): spell out the formula, not just the
// sign. Convention matches trade-math.getTradeExposure / the MtM page's
// computeMtmExposure / the "Repricing - MtM.xlsx" customer example — `+` = due to us,
// `−` = against us. Market value = (last marked price, else booking price) × quantity.
const EXP_LENT_TOOLTIP =
  'Exposure (Lent) = market value of lent shares − collateral held.\n+ = due to us, − = against us.';
const EXP_BORROWED_TOOLTIP =
  'Exposure (Borrowed) = collateral posted − market value of borrowed shares.\n+ = due to us, − = against us.';
const REPO_NET_EXPOSURE_TOOLTIP =
  'Net Exposure = market value − contract cash, summed per repo.\n+ = due to us, − = against us.';
// LR-307 AC-3: per-CP Repo Margin tooltip when a value is available.
// LR-385: sign convention canonicalised on docs/planning/LR-104/customer-decisions.md §5
// ("Final MC_Collateral Pool"), matching the GET /repo-margin-calls/balance/ endpoint that
// now feeds this cell. The earlier wording ("+ = CP owes us") was the opposite of §5 and the
// fixture — see LR-307 round-2 finding #1.
// LR-542 (feedback #63): the customer re-decided the DISPLAY sign to the billing convention —
// the cell now shows the flipped value (displayCashPool), so the tooltip flips with it. The
// stored/API sign is unchanged.
const REPO_MARGIN_TOOLTIP =
  'Repo cash pool posted with this counterparty, in the billing convention: − = we hold their cash (we pay interest on it), + = they hold ours (the CP pays us)';
// LR-307 AC-5: placeholder shown when the CP has open repo positions but no
// MTM has been booked yet, instead of hiding the cell entirely.
const REPO_MARGIN_PENDING_TOOLTIP =
  'This counterparty has open repo positions but no MTM run has been performed yet.';
const REPO_MARGIN_PENDING_LABEL = 'MTM not yet performed';
const REPO_MARGIN_SEVERAL_CCY_TOOLTIP =
  'The repo collateral sits in cash pools of several currencies, so there is no single total. The split by pool is in the cash pool history.';

/**
 * Returns true if the current `tradeTypes` filter exactly matches the preset.
 * Order-independent — sidebar filters are stored as arrays but semantically are sets.
 */
const filterMatches = <T,>(current: readonly T[], preset: readonly T[]): boolean =>
  current.length === preset.length && preset.every((v) => current.includes(v));

interface MetricsBarProps {
  /**
   * The active view's framework agreement, when the view is scoped to a single
   * counterparty. GMSLA shows only the Stock L/B section, GMRA only the Repo
   * section. null/undefined (multi-CP "All Trades", or unknown type) ⇒ both.
   */
  agreementType?: FrameworkAgreement | null;
  /** name -> FK id for the pill's server scope; from TradesPage. */
  cpNameToId?: Record<string, number>;
  /** False while a CP-scoped view is still waiting for the registry — gates the pill
   *  aggregate so it doesn't fetch a known-empty counterparty_ids=-1 slice (kvi-r3 #3). */
  cpScopeReady?: boolean;
}

export const MetricsBar: React.FC<MetricsBarProps> = ({ agreementType, cpNameToId = {}, cpScopeReady = true }) => {
  const { metrics, isFlashing, isLoading, isError } = useViewMetrics();
  // Until the first summary arrives every figure is a zero standing in for data; if it fails, a dash.
  const pending = isLoading ? 'loading' : isError ? 'failed' : undefined;
  const countOf = (n: number) =>
    pending === 'loading' ? <ValueSkeleton className="h-2.5 w-4" /> : pending === 'failed' ? '—' : n;
  const { showStockLB, showRepo } = agreementSections(agreementType);
  // LR-474 r5 #2: the agreement type may COLLAPSE an irrelevant section, but it must
  // never HIDE a section that actually carries trades. The registry holds ONE
  // agreement per counterparty, yet the book can hold trades of the OTHER family —
  // views scope by CP name, never by trade type — and since LR-634 that is a normal
  // booking rather than an oddity. So a section renders when the agreement allows it
  // OR when it has trades; the agreement only declutters an empty, irrelevant half.
  // GAP: the presence signal is the OPEN-book count, so between booking and
  // settlement a first other-family trade is visible in the table below while its
  // section here is still collapsed. Transient and money-safe; widening it to the
  // all-status count would undo LR-474 r6 #3 below, so it is a product call.
  // LR-474 r6 #3: gate on the OPEN-book count (not the all-status count) so a CP whose
  // only other-family trades are all pending/rejected doesn't surface an empty €0
  // section — that aligns the presence signal with the section's money cells (all
  // open-book) AND with the MTM bar's `rows.some(...)` (its rows exclude pending/rejected).
  const renderStockLB = showStockLB || metrics.stockLB.openCount > 0;
  const renderRepo = showRepo || metrics.repo.openCount > 0;
  // The bucket toggle (Stock L/B ⇄ Repo) is only a meaningful filter when BOTH
  // families are shown (All Trades, or a CP holding both) — with a single family it
  // is a one-value, useless filter, so its header renders as a static label (LR-518).
  const bothSections = renderStockLB && renderRepo;
  const views = useViewsStore((s) => s.views);
  const activeViewId = useViewsStore((s) => s.activeViewId);
  const activeView = views.find((v) => v.id === activeViewId);

  const localFilters = useTradesFilterStore((s) => s.localTradesFilters);
  const setLocalFilters = useTradesFilterStore((s) => s.setLocalTradesFilters);
  const viewScopedTradesData = useTradesDataStore((s) => s.viewScopedTradesData);
  const viewScopedSynced = useTradesDataStore((s) => s.viewScopedSynced);
  const viewScopedKey = useTradesDataStore((s) => s.viewScopedKey);
  const searchQuery = useTradesDataStore((s) => s.searchQuery);

  // Window-derived fallback for the pill while the server aggregate (serverPerCcy)
  // loads: view-scoped book, every filter except the pill's own settlement-ccy one
  // (so all settlement ccys stay pickable) and trade-type (LR-474 D3).
  const pillBasis = useMemo(
    () =>
      applyTradeSearch(
        applyLocalTradeFilters(viewScopedTradesData, { ...localFilters, settlementCurrencies: [] }, { skipTradeTypes: true }),
        searchQuery,
      ),
    [viewScopedTradesData, localFilters, searchQuery],
  );

  // Whole-book per-settlement-ccy aggregate — authoritative pill data (MetricsBar
  // only renders on the live blotter, so mode is 'trades').
  const pillParams = useMemo(
    () =>
      buildTradesPillParams({
        localFilters,
        searchQuery,
        viewCounterparties: activeView?.counterparties ?? [],
        cpNameToId,
        mode: 'trades',
      }),
    [localFilters, searchQuery, activeView, cpNameToId],
  );
  const { data: pillSummary } = useTradesSummary('settlement_ccy_totals', pillParams, cpScopeReady);
  const serverPerCcy = useMemo(
    () => pillSummary?.settlement_ccy_totals.map((t) => ({ ccy: t.ccy, count: t.count, value: parseFloat(t.value) })),
    [pillSummary],
  );
  const serverUnconvertibleCcys = useMemo(
    () => pillSummary?.settlement_ccy_totals.filter((t) => t.unconvertible).map((t) => t.ccy),
    [pillSummary],
  );

  const setSettlementCurrenciesFilter = useCallback(
    (settlementCurrencies: string[]) => setLocalFilters({ ...localFilters, settlementCurrencies }),
    [localFilters, setLocalFilters],
  );

  // LR-700: the Term pill's own slice — everything except its own `term_types`
  // predicate (so all three stay pickable). NOT the ccy pill's params: those also
  // drop `trade_types`, which would print counts disagreeing with the book under
  // an active Stock L/B / Repo preset.
  const termPillParams = useMemo(
    () =>
      buildTradesTermPillParams({
        localFilters,
        searchQuery,
        viewCounterparties: activeView?.counterparties ?? [],
        cpNameToId,
        mode: 'trades',
      }),
    [localFilters, searchQuery, activeView, cpNameToId],
  );
  const { data: termPillSummary } = useTradesSummary('term_type_totals', termPillParams, cpScopeReady);
  const setTermTypesFilter = useCallback(
    (termTypes: string[]) => setLocalFilters({ ...localFilters, termTypes: termTypes as TermType[] }),
    [localFilters, setLocalFilters],
  );

  // Which section is "active" based purely on the tradeTypes filter.
  // If the user applied an arbitrary subset via the sidebar (e.g. just ['lend']),
  // neither section lights up — that's correct: we don't misrepresent state.
  const { isAll, isStockLB, isRepo } = useMemo(() => {
    const t = localFilters.tradeTypes;
    return {
      isAll: t.length === 0,
      isStockLB: filterMatches(t, STOCK_LB_FILTER),
      isRepo: filterMatches(t, REPO_FILTER),
    };
  }, [localFilters.tradeTypes]);

  // Only dim a section when another preset is active — this makes it visually
  // clear a filter is on, without punishing the state of "no filter at all" or
  // "arbitrary sidebar filter".
  const anyPresetActive = isStockLB || isRepo;

  const setTradeTypesFilter = useCallback(
    (tradeTypes: TradeType[]) => {
      setLocalFilters({ ...localFilters, tradeTypes });
    },
    [localFilters, setLocalFilters],
  );

  // LR-686: the "Attention" chip is a shortcut for the sidebar Status filter set to
  // EXACTLY the attention statuses (pending + rejected). Like the family presets it
  // lights up only on an exact (order-independent) match, so an arbitrary sidebar
  // status selection is never misrepresented as the preset. Its count is the server
  // aggregate's `attention_count` on the same basis as the other counters (view ∩
  // sidebar filters, minus trade type — it ignores the family preset just as the
  // family badges ignore each other). The chip renders when there is something to
  // show OR the preset is on, so a stranded preset can always be cleared in place.
  const isAttention = useMemo(
    () => filterMatches(localFilters.statuses, ATTENTION_STATUSES),
    [localFilters.statuses],
  );
  const handleAttentionClick = useCallback(() => {
    setLocalFilters({ ...localFilters, statuses: isAttention ? [] : [...ATTENTION_STATUSES] });
  }, [isAttention, localFilters, setLocalFilters]);
  const attentionCount = metrics.attentionCount;
  const showAttention = attentionCount > 0 || isAttention;

  const handleAllClick = useCallback(() => {
    if (!isAll) setTradeTypesFilter([]);
  }, [isAll, setTradeTypesFilter]);

  const handleStockLBClick = useCallback(() => {
    // Toggle off if already active.
    setTradeTypesFilter(isStockLB ? [] : STOCK_LB_FILTER);
  }, [isStockLB, setTradeTypesFilter]);

  const handleRepoClick = useCallback(() => {
    setTradeTypesFilter(isRepo ? [] : REPO_FILTER);
  }, [isRepo, setTradeTypesFilter]);

  // LR-518 r1 #3: a trade-type preset that targets a family whose section is NOT
  // rendered strands the user — the surviving section's header is a static label
  // (bothSections=false) and on single-CP tabs the leading "All" button is hidden
  // too, so the table sits empty/dimmed with no in-bar recovery. Auto-clear the
  // stranded preset. NOTE the
  // full scope (kvi review #1): ANY path that empties the family un-renders its
  // section and clears the preset — not just a tab switch, but also a sidebar/
  // pill filter excluding every open row of that family. That is deliberate
  // strand-recovery (the alternative is an empty, un-clearable table), pending
  // product confirmation — see docs/reviews/ekh/LR-518-review-r2.md §5. Loop-safe:
  // the render gates come from useViewMetrics, whose basis skips the tradeTypes
  // filter, so clearing the preset never flips them back. The empty-book guard
  // (metrics.count spans BOTH families on the same basis) keeps a transient
  // refetch blip from eating a filter the arriving data would justify.
  // LR-518 r2 #0: TradesPage publishes the scoped set in an EFFECT, one commit
  // behind this component's agreementType prop on a view switch (and stale from
  // history mode on a history→live remount) — gates computed from a stale book
  // must not clear a preset the arriving data justifies. When a publish exists
  // (synced), it must belong to the CURRENT view in LIVE mode; the unsynced
  // fallback basis (bar rendered alone / unit tests) has no such lag.
  const bookEmpty = metrics.count === 0;
  const scopedStale = viewScopedSynced && viewScopedKey !== `${activeViewId}|live`;
  useEffect(() => {
    if (bookEmpty || scopedStale) return;
    if ((isStockLB && !renderStockLB) || (isRepo && !renderRepo)) {
      setTradeTypesFilter([]);
    }
  }, [bookEmpty, scopedStale, isStockLB, isRepo, renderStockLB, renderRepo, setTradeTypesFilter]);

  const viewLabel = activeView?.name ?? 'All Trades';

  // LR-307 AC-3/4/5: the per-CP "Repo Margin" cell is only meaningful in a
  // single-counterparty view AND only when that CP has open repo positions.
  // Otherwise nothing is shown — Stock-L/B-only views and All-trades view never
  // surface this cell (AC-4). When the CP has repo but cashpool data hasn't
  // arrived (no MTM run yet), we render an explicit pending placeholder
  // instead of hiding the cell (AC-5).
  const isSingleCpView = !!activeView && activeView.counterparties.length === 1;
  // LR-474 r7: gate the Repo Margin cell on the OPEN-book repo count, not the all-status
  // count — same signal as the section render-gate (renderRepo). Its pending placeholder
  // tooltip literally claims "open repo positions" (REPO_MARGIN_PENDING_TOOLTIP / LR-307
  // AC-5), so surfacing it for a CP whose repo trades are all pending/rejected (openCount
  // 0) would assert open positions that don't exist. The Repo button's count badge keeps
  // using metrics.repo.count to surface pending/rejected for operator follow-up.
  const showRepoMargin = isSingleCpView && metrics.repo.openCount > 0;
  const repoMarginSeveralCcy = metrics.repo.cashpoolInSeveralCurrencies;
  const repoMarginPending = metrics.repo.cashpool == null && !repoMarginSeveralCcy;
  // LR-542 (feedback #63): display the pool billing-signed (flipped vs the stored value the
  // balance endpoint serves). withPoolSign keeps the explicit "+" on positives — on this cell
  // the sign IS the information, so a bare figure must not read as unsigned.
  const repoMarginDisplayed =
    metrics.repo.cashpool == null ? null : displayCashPool(metrics.repo.cashpool);
  const repoMarginValueLabel = repoMarginSeveralCcy
    ? '—'
    : repoMarginDisplayed == null
      ? REPO_MARGIN_PENDING_LABEL
      : withPoolSign(
          repoMarginDisplayed,
          formatCurrency(repoMarginDisplayed, metrics.repo.cashpoolCurrency ?? undefined),
        );

  const sectionHeaderBase =
    'text-xs font-semibold uppercase tracking-wide whitespace-nowrap px-2 py-1 rounded transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500';

  // LR-469 / kvi #4: each money figure arrives pre-formatted from use-view-metrics —
  // native with its ISO symbol when the underlying trades share ONE currency (data-
  // driven, so a pure-EUR book labels itself '€' even with no filter), the `≈ base`
  // roll-up when they span several, with the full per-currency breakdown rendered in
  // the MoneyMetricCell hover card. Exposure cells pass a sign-convention formula
  // shown as a footnote in that card.

  return (
    <div data-tour="trades-metrics-bar" className="flex items-center border-t border-b border-slate-300 bg-white px-2 shadow-[0_2px_6px_-1px_rgba(0,0,0,0.08),0_1px_3px_-1px_rgba(0,0,0,0.06)]">
      {/* Metrics scroll in their own region and the pills stay outside it - see MtmAggregateBar. */}
      <div className="scroll-x-thin flex min-w-0 flex-1 items-center overflow-x-auto">
        {/* Leading "All / clear filter" control — shown only on the All Trades view. On
            a per-counterparty tab the view name just duplicates the tab label (LR-518),
            and issue-1 already removes the bar's trade-type toggles there, so the whole
            control is hidden on single-counterparty tabs. */}
        {!isSingleCpView && (
          <>
            <button
              type="button"
              onClick={handleAllClick}
              className={clsx(
                'flex shrink-0 items-center px-3 py-1.5 rounded transition-colors',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500',
                isAll
                  ? 'cursor-default bg-blue-100'
                  : 'cursor-pointer hover:bg-slate-50',
              )}
              title={isAll ? undefined : 'Show all — clear trade type filter'}
              aria-pressed={isAll}
            >
              <span
                className={clsx(
                  'text-sm truncate max-w-[200px]',
                  isAll ? 'font-semibold text-blue-800' : 'font-medium text-slate-500',
                )}
              >
                {viewLabel}
              </span>
              <span
                aria-hidden="true"
                className="ml-1.5 text-xs tabular-nums text-slate-400"
              >
                {countOf(metrics.count)}
              </span>
            </button>

            <span className="text-slate-300 select-none px-1 text-lg">·</span>
          </>
        )}

        {/* LR-686: attention rows are no longer bucketed above the book — this chip is
            how they stay findable: the count, and one click to isolate them. */}
        {showAttention && (
          <>
            <button
              type="button"
              onClick={handleAttentionClick}
              className={clsx(
                sectionHeaderBase,
                'flex items-center gap-1',
                isAttention
                  ? 'bg-amber-100 text-amber-900'
                  : 'text-amber-700 hover:bg-amber-50 hover:text-amber-900 cursor-pointer',
              )}
              title={
                isAttention
                  ? 'Click to clear — show all statuses'
                  : 'Show only positions that require attention (Pending Trade Date, Rejected)'
              }
              aria-pressed={isAttention}
              data-testid="metrics-attention-chip"
            >
              <ExclamationTriangleIcon className="h-3.5 w-3.5" aria-hidden="true" />
              Attention
              <span
                aria-hidden="true"
                className={clsx(
                  'ml-0.5 text-[10px] tabular-nums font-normal',
                  isAttention ? 'text-amber-800' : 'text-amber-600',
                )}
              >
                {countOf(attentionCount)}
              </span>
            </button>
            <span className="text-slate-300 select-none px-1 text-lg">·</span>
          </>
        )}

        {/* Stock L/B group — collapsed for a GMRA-only CP view UNLESS it carries
            stock-lend/borrow trades (LR-474 r5 #2 — never hide extant trades). */}
        {renderStockLB && (
          <div
            className={clsx(
              'flex items-center',
              anyPresetActive && !isStockLB && 'opacity-60',
            )}
            role="group"
            aria-label="Stock Lend/Borrow metrics"
          >
            {/* Bucket toggle only when both families are shown; else a static label —
                a one-value filter is useless (LR-518). */}
            {bothSections ? (
              <button
                type="button"
                onClick={handleStockLBClick}
                className={clsx(
                  sectionHeaderBase,
                  isStockLB
                    ? 'bg-blue-100 text-blue-800 font-semibold'
                    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900 cursor-pointer',
                )}
                title={
                  isStockLB
                    ? 'Click to clear — show all trade types'
                    : 'Filter to Stock Lend & Borrow'
                }
                aria-pressed={isStockLB}
              >
                Stock L/B
                <span
                  aria-hidden="true"
                  className="ml-1.5 text-[10px] tabular-nums font-normal text-slate-400"
                >
                  {countOf(metrics.stockLB.count)}
                </span>
              </button>
            ) : (
              <span className={clsx(sectionHeaderBase, 'text-slate-500')}>
                Stock L/B
                <span
                  aria-hidden="true"
                  className="ml-1.5 text-[10px] tabular-nums font-normal text-slate-400"
                >
                  {countOf(metrics.stockLB.count)}
                </span>
              </span>
            )}
            <MoneyMetricCell label="Total Lent" total={metrics.stockLB.totalValueLent} isFlashing={isFlashing} pending={pending} />
            <MoneyMetricCell label="Total Borrowed" total={metrics.stockLB.totalValueBorrowed} isFlashing={isFlashing} pending={pending} />
            <MoneyMetricCell
              label="Exp. Lent"
              total={metrics.stockLB.exposureLent}
              alert={metrics.stockLB.exposureLent.value < 0}
              isFlashing={isFlashing}
              pending={pending}
              formula={EXP_LENT_TOOLTIP}
            />
            <MoneyMetricCell
              label="Exp. Borrowed"
              total={metrics.stockLB.exposureBorrowed}
              alert={metrics.stockLB.exposureBorrowed.value < 0}
              isFlashing={isFlashing}
              pending={pending}
              formula={EXP_BORROWED_TOOLTIP}
            />
          </div>
        )}

        {renderStockLB && renderRepo && (
          <span aria-hidden="true" className="mx-2 h-6 w-px shrink-0 bg-slate-300 select-none" />
        )}

        {/* Repo group — collapsed for a GMSLA-only CP view UNLESS it carries
            repo/reverse-repo trades (LR-474 r5 #2 — never hide extant trades). */}
        {renderRepo && (
          <div
            className={clsx(
              'flex items-center',
              anyPresetActive && !isRepo && 'opacity-60',
            )}
            role="group"
            aria-label="Repo metrics"
          >
            {bothSections ? (
              <button
                type="button"
                onClick={handleRepoClick}
                className={clsx(
                  sectionHeaderBase,
                  isRepo
                    ? 'bg-blue-100 text-blue-800 font-semibold'
                    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900 cursor-pointer',
                )}
                title={
                  isRepo
                    ? 'Click to clear — show all trade types'
                    : 'Filter to Repo & Reverse Repo'
                }
                aria-pressed={isRepo}
              >
                Repo
                <span
                  aria-hidden="true"
                  className="ml-1.5 text-[10px] tabular-nums font-normal text-slate-400"
                >
                  {countOf(metrics.repo.count)}
                </span>
              </button>
            ) : (
              <span className={clsx(sectionHeaderBase, 'text-slate-500')}>
                Repo
                <span
                  aria-hidden="true"
                  className="ml-1.5 text-[10px] tabular-nums font-normal text-slate-400"
                >
                  {countOf(metrics.repo.count)}
                </span>
              </span>
            )}
            <MoneyMetricCell label="Total Value" total={metrics.repo.totalValue} isFlashing={isFlashing} pending={pending} />
            <MoneyMetricCell
              label="Net Exposure"
              total={metrics.repo.netExposure}
              alert={metrics.repo.netExposure.value < 0}
              isFlashing={isFlashing}
              pending={pending}
              formula={REPO_NET_EXPOSURE_TOOLTIP}
            />
            {/* LR-542 review kvi #1: the alert dot stays — it flags the RISK STATE "the CP holds
                our cash" (counterparty credit exposure), which is orthogonal to how the figure is
                signed. The flip only moved that state to the other side of the axis: it used to be
                stored < 0, it now displays as > 0. The dot is a separate red marker (MetricCell
                never colours the figure), and alertLabel spells the state out for screen readers —
                the sign glyph alone is not accessible. Dropping the indicator entirely is the open
                customer question Q2; until answered, keep the signal (safe default). */}
            {showRepoMargin && (
              <MetricCell
                label="Repo Margin"
                value={repoMarginValueLabel}
                alert={repoMarginDisplayed != null && repoMarginDisplayed > 0}
                alertLabel="The counterparty holds our cash"
                isFlashing={isFlashing}
                tooltip={
                  repoMarginSeveralCcy
                    ? REPO_MARGIN_SEVERAL_CCY_TOOLTIP
                    : repoMarginPending
                      ? REPO_MARGIN_PENDING_TOOLTIP
                      : REPO_MARGIN_TOOLTIP
                }
              />
            )}
          </div>
        )}
      </div>

      <TermFilterPill
        className="mr-1"
        totals={termPillSummary?.term_type_totals}
        selected={localFilters.termTypes}
        onChange={setTermTypesFilter}
      />

      <CurrencyFilterPill
        className="mr-1"
        rows={pillBasis}
        serverPerCcy={serverPerCcy}
        serverUnconvertibleCcys={serverUnconvertibleCcys}
        selected={localFilters.settlementCurrencies}
        onChange={setSettlementCurrenciesFilter}
      />
    </div>
  );
};
